import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AppProvider, useApp } from "@/context/AppContext";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import Instructions from "@/pages/Instructions";
import Upload from "@/pages/Upload";
import Result from "@/pages/Result";
import Pricing from "@/pages/Pricing";
import Profile from "@/pages/Profile";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import GoogleCallback from "@/pages/GoogleCallback";
import PolicyPage from "@/pages/PolicyPage";
import { useCopyProtection } from "@/lib/copyProtection";
import Library from "@/pages/Library";
import QuestionDetail from "@/pages/QuestionDetail";
import RefundPolicy from "@/pages/RefundPolicy";
import Admin from "@/pages/Admin";
import Maintenance from "@/pages/Maintenance";

function ProtectedRoute({ children }) {
  const { user, authLoading } = useApp();
  if (authLoading) {
    return <div className="app-bg min-h-screen grid place-items-center text-amber-200 font-serif">…</div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  const { settings, user, authLoading } = useApp();
  const [adminBypass, setAdminBypass] = React.useState(false);
  useCopyProtection(location.pathname);
  // Handle OAuth callback synchronously (uses reactive hash)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  if (location.pathname === "/auth/google") {
    return <GoogleCallback />;
  }
  if (settings?.maintenance && !authLoading && !(user?.is_admin && (adminBypass || location.pathname === "/admin"))) {
    if (!user?.is_admin && location.pathname !== "/login") {
      return <Maintenance />;
    }
    if (user?.is_admin) return <Maintenance adminPreview onEnter={() => setAdminBypass(true)} />;
  }
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/instructions" element={<Instructions />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/library" element={<Library />} />
        <Route path="/library/:qid" element={<QuestionDetail />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />
        {["contact", "privacy-policy", "cancellation-policy", "shipping-policy", "about", "services"].map((slug) => (
          <Route key={slug} path={`/${slug}`} element={<PolicyPage slug={slug} />} />
        ))}
        <Route path="/admin" element={<Admin />} />
        <Route path="/login" element={<Login />} />
        <Route path="/upload" element={<ProtectedRoute><Upload /></ProtectedRoute>} />
        <Route path="/result/:id" element={<ProtectedRoute><Result /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-center" theme="dark" richColors />
      </BrowserRouter>
    </AppProvider>
  );
}

export default App;
